import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Stack,
  Menu,
  MenuItem,
  Checkbox,
  Tooltip,
} from '@mui/material';
import { Close, Delete, AccessTime, Person, Business, FilterList } from '@mui/icons-material';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import { startOfDay, isBefore, format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { apiService } from '../services/apiService';
import { API_ENDPOINTS } from '../config/api';
import { colors } from '../theme/theme';
import DuplicateSessionConfirmDialog from './DuplicateSessionConfirmDialog';
import { getDuplicateSessionError, dropSkipped, DuplicateSessionInfo, DuplicateEntry } from '../utils/duplicateSession';

const formatDateIST = (date: Date): string => {
  const ist = new Date(date.getTime() + 5.5 * 60 * 60 * 1000);
  return ist.toISOString().slice(0, 10);
};

// Copy the source session's exact stored UTC time onto the chosen target date,
// as UTC. Sessions are stored in true UTC, so keeping the same UTC time-of-day
// preserves the wall-clock time (a 9:00 AM IST class stays 9:00 AM IST on the new
// day). This restores the pre-2026-06-29 behaviour — commit c82c7be regressed it
// by re-stamping the UTC time with +05:30, which shifted every copy by −5:30.
const buildCopyDateUTC = (targetDate: Date, originalDate: Date): string => {
  return new Date(
    Date.UTC(
      targetDate.getFullYear(),
      targetDate.getMonth(),
      targetDate.getDate(),
      originalDate.getUTCHours(),
      originalDate.getUTCMinutes(),
      originalDate.getUTCSeconds(),
    ),
  ).toISOString();
};

interface Session {
  _id: string;
  className: string;
  date: string;
  slots: number;
  availableSlots: number;
  price: number;
  duration: number;
  image?: string;
  class?: {
    _id: string;
  };
  studio: {
    _id: string;
    studioName: string;
  };
  instructor: {
    _id: string;
    name: string;
  };
  /** Per-session trial settings — may differ from the class default. */
  isTrialEnabled?: boolean;
  trialPrice?: number;
  classType: {
    _id: string;
    name: string;
  };
}

interface Studio {
  _id: string;
  studioName: string;
}

interface CopyDayDialogProps {
  open: boolean;
  onClose: () => void;
  targetDate: Date;
  onSuccess: () => void;
  /**
   * The ONE location the copy is created at ("Create at" under All
   * locations). When set, the source day is read from that location only —
   * otherwise the aggregate list would offer every location's sessions and
   * write them all to this one.
   */
  studioId?: string;
}

const CopyDayDialog: React.FC<CopyDayDialogProps> = ({ open, onClose, targetDate, onSuccess, studioId: targetStudioId }) => {
  // Starts unset on purpose — nothing is pre-selected, not even today. The
  // calendar just opens on the month of `targetDate` (referenceDate below); the
  // operator must actively pick a target day before Review lights up.
  const [copyTargetDate, setCopyTargetDate] = useState<Date | null>(null);
  const [sourceDate, setSourceDate] = useState<Date | null>(null);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [selectedSessions, setSelectedSessions] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [isCopying, setIsCopying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Duplicate-session confirm: the built copy payload is parked here when the
  // backend rejects it with DUPLICATE_SESSION, then re-sent with allowDuplicate.
  const [dupInfo, setDupInfo] = useState<DuplicateSessionInfo | null>(null);
  const [dupLoading, setDupLoading] = useState(false);
  const [pendingCopy, setPendingCopy] = useState<{ studioId: string; sessions: any[]; allowPast?: boolean } | null>(null);
  // Partial-success notice: some sessions were copied, others skipped because
  // their class had been deleted. Shown without closing so the user reads it.
  const [warning, setWarning] = useState<string | null>(null);
  const [studios, setStudios] = useState<Studio[]>([]);
  const [selectedStudios, setSelectedStudios] = useState<Set<string>>(new Set());
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
  const [isLoadingStudios, setIsLoadingStudios] = useState(false);
  const [allSessions, setAllSessions] = useState<Session[]>([]);
  // Sessions already fetched this dialog session, keyed by IST date string. Revisiting
  // a date reads from here instead of re-hitting the API; the dot on the source
  // calendar is also derived from these keys, so no separate dot-fetching is needed.
  const [dayCache, setDayCache] = useState<Record<string, Session[]>>({});
  // Second step: an explicit "are you sure?" with a preview of exactly what will
  // be created, mirroring the bulk edit / delete workspace. The primary button
  // opens this; the real copy only runs from its Confirm button.
  const [confirmOpen, setConfirmOpen] = useState(false);

  // The rows the confirm-step preview lists — the selected sessions, in the
  // order they appear in the list.
  const selectedSessionList = React.useMemo(
    () => sessions.filter((s) => selectedSessions.has(s._id)),
    [sessions, selectedSessions],
  );

  // On open: sync the target date, default the source date to today (so today's
  // sessions load immediately via the fetchSessions effect below), and fetch the
  // studio filter list. Clicking any other source date fetches just that one date.
  useEffect(() => {
    if (open) {
      setCopyTargetDate(null);
      setSourceDate(startOfDay(new Date()));
      setConfirmOpen(false);
      fetchStudios();
    } else {
      setSelectedStudios(new Set());
      setDayCache({});
    }
  }, [open, targetDate]);

  // Fetch sessions when source date is selected
  useEffect(() => {
    if (sourceDate) {
      fetchSessions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceDate]);

  // Apply studio filter when it changes
  useEffect(() => {
    if (sourceDate && allSessions.length > 0) {
      const filteredSessions = selectedStudios.size > 0 && studios.length > 0
        ? allSessions.filter((s) => selectedStudios.has(s.studio._id))
        : allSessions;
      
      setSessions(filteredSessions);
      
      // Update selected sessions to only include visible (filtered) sessions
      const visibleSessionIds = new Set<string>(filteredSessions.map((s) => s._id));
      setSelectedSessions((prevSelected) => {
        const updatedSelected = new Set<string>();
        prevSelected.forEach((id) => {
          if (visibleSessionIds.has(id)) {
            updatedSelected.add(id);
          }
        });
        return updatedSelected;
      });
    }
  }, [selectedStudios, allSessions, sourceDate, studios]);

  const fetchStudios = async () => {
    // Studio users only manage their own studio — no need to fetch studios list
    if (apiService.isStudio()) return;
    try {
      setIsLoadingStudios(true);
      const response = await apiService.getStudios();
      const studiosData = response.data || [];
      setStudios(studiosData);
      const allStudioIds = new Set<string>(studiosData.map((s: Studio) => s._id));
      setSelectedStudios(allStudioIds);
    } catch (err: any) {
      console.error('Error fetching studios:', err);
    } finally {
      setIsLoadingStudios(false);
    }
  };

  // Applies the studio filter and selects everything visible by default — shared by
  // both the cache-hit and freshly-fetched paths in fetchSessions below.
  const applySessions = (fetchedSessions: Session[]) => {
    setAllSessions(fetchedSessions);
    const filteredSessions = selectedStudios.size > 0 && studios.length > 0
      ? fetchedSessions.filter((s) => selectedStudios.has(s.studio._id))
      : fetchedSessions;
    setSessions(filteredSessions);
    setSelectedSessions(new Set(filteredSessions.map((s) => s._id)));
  };

  const fetchSessions = async () => {
    if (!sourceDate) return;
    const dateStr = formatDateIST(sourceDate);

    const cached = dayCache[dateStr];
    if (cached) {
      setError(null);
      applySessions(cached);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      // Use studio-aware method (branches internally for studio vs admin)
      const response = apiService.isStudio()
        ? await apiService.getActiveSessionsByDate({ date: dateStr }, targetStudioId)
        : await apiService.get(API_ENDPOINTS.SESSIONS.ALL_BY_DATE, { params: { date: dateStr } });
      // Belt and braces on the pinned read: only the target location's
      // sessions may be copied to it.
      const fetchedSessions: Session[] = (response.sessions || []).filter(
        (s: Session) => !targetStudioId || s.studio?._id === targetStudioId,
      );
      setDayCache((prev) => ({ ...prev, [dateStr]: fetchedSessions }));
      applySessions(fetchedSessions);
    } catch (err: any) {
      console.error('Error fetching sessions:', err);
      setError(err.message || 'Failed to fetch sessions');
      setSessions([]);
      setAllSessions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleSessionSelection = (sessionId: string) => {
    const newSelected = new Set(selectedSessions);
    if (newSelected.has(sessionId)) {
      newSelected.delete(sessionId);
    } else {
      newSelected.add(sessionId);
    }
    setSelectedSessions(newSelected);
  };

  const handleSelectAll = () => {
    const allSessionIds = new Set<string>(sessions.map((s) => s._id));
    setSelectedSessions(allSessionIds);
  };

  const handleDeselectAll = () => {
    setSelectedSessions(new Set());
  };

  const toggleStudioFilter = (studioId: string) => {
    const newSelected = new Set(selectedStudios);
    if (newSelected.has(studioId)) {
      newSelected.delete(studioId);
    } else {
      newSelected.add(studioId);
    }
    setSelectedStudios(newSelected);
    // Filter will be applied via useEffect
  };

  const handleSelectAllStudios = () => {
    const allStudioIds = new Set<string>(studios.map((s) => s._id));
    setSelectedStudios(allStudioIds);
  };

  const handleDeselectAllStudios = () => {
    setSelectedStudios(new Set());
  };

  // Selected sessions whose copy would START BEFORE NOW on the target date
  // (copying to today, or a slot that has passed). The server skips/refuses
  // them unless the operator confirms — so they are shown on the review step
  // instead of disappearing as "skipped" afterwards.
  const startedIds = React.useMemo(() => {
    const ids = new Set<string>();
    if (!copyTargetDate) return ids;
    const now = Date.now();
    for (const s of sessions) {
      if (!selectedSessions.has(s._id)) continue;
      const at = new Date(buildCopyDateUTC(copyTargetDate, new Date(s.date))).getTime();
      if (at < now) ids.add(s._id);
    }
    return ids;
    // confirmOpen: re-evaluated when the review step opens (time moves on).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessions, selectedSessions, copyTargetDate, confirmOpen]);

  /**
   * @param includeStarted create the sessions whose start time on the target
   * day has already passed (sent with allowPastSessions — the server skips or
   * refuses them otherwise). false = copy only the ones still ahead.
   */
  const handleCopyDay = async (includeStarted = true) => {
    if (!copyTargetDate) {
      setError('Please select a target date');
      return;
    }
    if (selectedSessions.size === 0) {
      setError('Please select at least one session to copy');
      return;
    }

    try {
      setIsCopying(true);
      setError(null);

      // Get selected sessions and prepare them for copying
      const sessionsToCopy = sessions
        .filter((s) => selectedSessions.has(s._id))
        .filter((s) => includeStarted || !startedIds.has(s._id))
        .map((session) => {
          const originalDate = new Date(session.date);

          return {
            // Backend expects `class` (class id), not `classId`
            class: session.class?._id || session.class,
            date: buildCopyDateUTC(copyTargetDate, originalDate),
            slots: session.slots,
            // Preserve original isLive when available; default to live
            isLive: (session as any).isLive ?? true,

            // ── Carry what the SESSION says, not what its class says ───────
            // A session may differ from its class on instructor, price and the
            // trial settings — all four are editable per session. Sending only
            // {class, date, slots} made the backend re-derive them from the
            // class, which silently rewrote the copied week: at Laya every
            // Thursday morning came back assigned to the class default
            // (Shamlee) instead of Shilpa, and trial came back ON for sessions
            // that had it deliberately switched off.
            //
            // Each is sent only when known — an omitted field still falls back
            // to the class server-side, which is the old behaviour.
            ...(session.instructor?._id ? { instructor: session.instructor._id } : {}),
            ...(typeof session.price === 'number' ? { price: session.price } : {}),
            ...(typeof session.isTrialEnabled === 'boolean'
              ? { isTrialEnabled: session.isTrialEnabled }
              : {}),
            ...(typeof session.trialPrice === 'number'
              ? { trialPrice: session.trialPrice }
              : {}),
            // Optional flag, only include if present to match CreateSessionDto
            ...(typeof (session as any).onlyForFemales === 'boolean'
              ? { onlyForFemales: (session as any).onlyForFemales }
              : {}),
            // IMPORTANT: Preserve original studio ID for each session
            // This ensures sessions from different studios keep their correct studio
            studio: session.studio._id,
          };
        });

      // Get studio ID from the first session (fallback, but each session has its own studio now)
      const studioId = targetStudioId || sessions[0].studio._id;

      if (sessionsToCopy.length === 0) {
        setError('Every selected session has already started on the target date. Choose "Copy all anyway" or another date.');
        return;
      }
      const allowPast = includeStarted && startedIds.size > 0;

      try {
        await postCopy(studioId, sessionsToCopy, false, allowPast);
      } catch (err: any) {
        const dup = getDuplicateSessionError(err);
        if (dup) {
          // Park the built payload; the confirm re-sends it with allowDuplicate.
          // Drop the confirm step so the duplicate dialog is the only thing up.
          setConfirmOpen(false);
          setPendingCopy({ studioId, sessions: sessionsToCopy, allowPast });
          setDupInfo(dup);
          return;
        }
        throw err;
      }
    } catch (err: any) {
      console.error('Error copying sessions:', err);
      const msg = err?.response?.data?.message || err?.message || 'Failed to copy sessions';
      setError(msg);
    } finally {
      setIsCopying(false);
    }
  };

  /**
   * Posts the copy and applies the shared success/skipped handling. Throws on
   * failure so the caller can special-case a DUPLICATE_SESSION 409.
   */
  const postCopy = async (
    studioId: string,
    sessionsToCopy: any[],
    allowDuplicate: boolean,
    allowPast = false,
  ) => {
    const res: any = await apiService.createMultipleSessions(studioId, {
      sessions: sessionsToCopy,
      ...(allowDuplicate ? { allowDuplicate: true } : {}),
      // The operator chose to create sessions that have already started.
      ...(allowPast ? { allowPastSessions: true } : {}),
    });

    // The POST went through — drop the confirm step regardless of the skipped
    // outcome below, so its (now stale) preview isn't left sitting on top.
    setConfirmOpen(false);

    // The backend no longer aborts the whole copy when a class was deleted — it
    // skips those sessions and reports them. Surface the notice (and keep the
    // dialog open) rather than silently closing, but still refresh the parent
    // since the other sessions WERE created.
    const skippedCount = res?.skipped?.length ?? 0;
    if (skippedCount > 0) {
      onSuccess();
      setWarning(
        res?.message ||
          `${skippedCount} session${skippedCount === 1 ? '' : 's'} were not copied — their class has been deleted.`,
      );
      setSelectedSessions(new Set());
    } else {
      onSuccess();
      handleClose();
    }
  };

  const handleConfirmDuplicate = async (skip: DuplicateEntry[]) => {
    if (!pendingCopy) return;
    const sessions = dropSkipped(pendingCopy.sessions, skip);
    if (sessions.length === 0) {
      // Every selected session was a collision the operator then unchecked.
      setDupInfo(null);
      setPendingCopy(null);
      return;
    }
    setDupLoading(true);
    setError(null);
    try {
      await postCopy(pendingCopy.studioId, sessions, true, pendingCopy.allowPast);
      setDupInfo(null);
      setPendingCopy(null);
    } catch (err: any) {
      console.error('Error copying sessions:', err);
      setError(err?.response?.data?.message || err?.message || 'Failed to copy sessions');
      setDupInfo(null);
      setPendingCopy(null);
    } finally {
      setDupLoading(false);
    }
  };

  const handleClose = () => {
    setSourceDate(null);
    setSessions([]);
    setAllSessions([]);
    setSelectedSessions(new Set());
    setError(null);
    setWarning(null);
    setFilterAnchorEl(null);
    setConfirmOpen(false);
    onClose();
  };

  // Primary button: validate, then open the confirm step. The copy itself runs
  // from the confirm dialog's Confirm button.
  const handleRequestCopy = () => {
    if (!copyTargetDate) {
      setError('Please select a target date');
      return;
    }
    if (!sourceDate || selectedSessions.size === 0) {
      setError('Please select at least one session to copy');
      return;
    }
    setError(null);
    setConfirmOpen(true);
  };

  const shouldDisableSourceDate = (date: Date) => {
    // Source: allow today and past only — future dates cannot be copied from
    const today = startOfDay(new Date());
    const dateToCheck = startOfDay(date);
    return dateToCheck > today;
  };

  const shouldDisableTargetDate = (date: Date) => {
    // Target: allow today and future only — past dates cannot be copied to
    const today = startOfDay(new Date());
    const dateToCheck = startOfDay(date);
    return isBefore(dateToCheck, today);
  };

  const hasCachedSessions = (date: Date) => (dayCache[formatDateIST(date)]?.length ?? 0) > 0;

  const ServerDay = (props: PickersDayProps) => {
    const { day, outsideCurrentMonth, ...other } = props;
    const showDot = !outsideCurrentMonth && hasCachedSessions(day);
    return (
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        <PickersDay {...other} day={day} outsideCurrentMonth={outsideCurrentMonth} />
        {showDot && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 2,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 4,
              height: 4,
              borderRadius: '50%',
              backgroundColor: colors.primary,
            }}
          />
        )}
      </Box>
    );
  };

  const formatSessionTime = (dateString: string) => {
    const date = new Date(dateString);
    return formatInTimeZone(date, 'Asia/Kolkata', 'hh:mm a');
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight={700}>
            Copy Day
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          {warning && (
            <Alert severity="warning" onClose={() => setWarning(null)}>
              {warning}
            </Alert>
          )}
          {/* Source (left) + Target (right) — both dates visible together */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            {/* Source — LEFT */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Source Date
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Copy sessions from this day (today or past)
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateCalendar
                  value={sourceDate}
                  onChange={(date) => setSourceDate(date)}
                  shouldDisableDate={shouldDisableSourceDate}
                  slots={{ day: ServerDay }}
                  sx={{
                    width: '100%',
                    '& .MuiPickersCalendarHeader-root': { paddingLeft: 1, paddingRight: 1 },
                  }}
                />
              </LocalizationProvider>
            </Box>

            <Divider flexItem orientation="vertical" sx={{ display: { xs: 'none', md: 'block' } }} />

            {/* Target — RIGHT */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Target Date
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Create the copied sessions on this day (today or future)
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateCalendar
                  value={copyTargetDate}
                  referenceDate={targetDate}
                  onChange={(date) => date && setCopyTargetDate(date)}
                  shouldDisableDate={shouldDisableTargetDate}
                  sx={{
                    width: '100%',
                    '& .MuiPickersCalendarHeader-root': { paddingLeft: 1, paddingRight: 1 },
                  }}
                />
              </LocalizationProvider>
              {!copyTargetDate && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Pick a target date to continue.
                </Typography>
              )}
            </Box>
          </Box>

          <Divider />

          {/* Sessions List */}
          {sourceDate && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Select Sessions to Copy
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {!apiService.isStudio() && (
                    <Tooltip title="Filter by Studio">
                      <IconButton
                        onClick={(e) => setFilterAnchorEl(e.currentTarget)}
                        color={selectedStudios.size > 0 && selectedStudios.size < studios.length ? 'primary' : 'default'}
                        size="small"
                      >
                        <FilterList />
                      </IconButton>
                    </Tooltip>
                  )}
                  {sessions.length > 0 && (
                    <>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handleSelectAll}
                        disabled={selectedSessions.size === sessions.length}
                      >
                        Select All
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handleDeselectAll}
                        disabled={selectedSessions.size === 0}
                      >
                        Deselect All
                      </Button>
                    </>
                  )}
                </Box>
              </Box>

              {/* Studio Filter Menu */}
              <Menu
                anchorEl={filterAnchorEl}
                open={Boolean(filterAnchorEl)}
                onClose={() => setFilterAnchorEl(null)}
                PaperProps={{
                  sx: { maxHeight: 400, width: 300 }
                }}
              >
                <Box sx={{ p: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, px: 1 }}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      Filter by Studio
                    </Typography>
                    <Box>
                      <Button size="small" onClick={handleSelectAllStudios} sx={{ minWidth: 'auto', px: 1 }}>
                        All
                      </Button>
                      <Button size="small" onClick={handleDeselectAllStudios} sx={{ minWidth: 'auto', px: 1 }}>
                        None
                      </Button>
                    </Box>
                  </Box>
                  <Divider sx={{ mb: 1 }} />
                  {isLoadingStudios ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : (
                    studios.map((studio) => (
                      <MenuItem
                        key={studio._id}
                        onClick={() => toggleStudioFilter(studio._id)}
                        dense
                      >
                        <Checkbox
                          checked={selectedStudios.has(studio._id)}
                          size="small"
                          sx={{ p: 0.5 }}
                        />
                        <Typography variant="body2" sx={{ ml: 1 }}>
                          {studio.studioName}
                        </Typography>
                      </MenuItem>
                    ))
                  )}
                  {selectedStudios.size > 0 && (
                    <>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
                        {selectedStudios.size} of {studios.length} studios selected
                      </Typography>
                    </>
                  )}
                </Box>
              </Menu>

              {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : error ? (
                <Alert severity="error">{error}</Alert>
              ) : sessions.length === 0 ? (
                <Alert severity="info">No sessions found for the selected date</Alert>
              ) : (
                <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                  <Stack spacing={2}>
                    {sessions.map((session) => (
                      <Card
                        key={session._id}
                        sx={{
                          cursor: 'pointer',
                          border: `2px solid ${selectedSessions.has(session._id) ? colors.primary : 'transparent'}`,
                          backgroundColor: selectedSessions.has(session._id) ? colors.primary + '08' : 'inherit',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            boxShadow: 2,
                          },
                        }}
                        onClick={() => toggleSessionSelection(session._id)}
                      >
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="subtitle1" fontWeight={700}>
                                {session.className}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1, fontWeight: 500 }}>
                                <Business fontSize="inherit" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                                {session.studio?.studioName || 'N/A'}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                                <Chip
                                  icon={<AccessTime />}
                                  label={formatSessionTime(session.date)}
                                  size="small"
                                  variant="outlined"
                                />
                                <Chip
                                  icon={<Person />}
                                  label={`${session.slots} slots`}
                                  size="small"
                                  variant="outlined"
                                />
                                <Chip
                                  label={`₹${session.price}`}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                              </Box>
                            </Box>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleSessionSelection(session._id);
                              }}
                              color={selectedSessions.has(session._id) ? 'primary' : 'default'}
                            >
                              {selectedSessions.has(session._id) ? (
                                <Delete color="error" />
                              ) : (
                                <Delete />
                              )}
                            </IconButton>
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                </Box>
              )}

              {sessions.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {selectedSessions.size} of {sessions.length} sessions selected
                </Typography>
              )}
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isCopying}>
          Cancel
        </Button>
        <Button
          onClick={handleRequestCopy}
          variant="contained"
          disabled={!copyTargetDate || !sourceDate || selectedSessions.size === 0 || isCopying}
        >
          {`Review ${selectedSessions.size} Session${selectedSessions.size !== 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>

    {/* Step 2 — "are you sure?" with a preview of exactly what gets created */}
    <Dialog
      open={confirmOpen}
      onClose={() => { if (!isCopying) setConfirmOpen(false); }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight={700}>Confirm copy</Typography>
          <IconButton onClick={() => setConfirmOpen(false)} size="small" disabled={isCopying}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
          )}
          <Alert severity="info" icon={false}>
            <Typography variant="body2">
              Copy <strong>{selectedSessionList.length} session{selectedSessionList.length !== 1 ? 's' : ''}</strong> from{' '}
              <strong>{sourceDate ? format(sourceDate, 'EEE, dd MMM yyyy') : '—'}</strong>{' '}
              to <strong>{copyTargetDate ? format(copyTargetDate, 'EEE, dd MMM yyyy') : '—'}</strong>?
            </Typography>
            <Typography variant="caption" color="text.secondary">
              New sessions are created at the same time of day. Existing sessions and bookings are not touched.
            </Typography>
          </Alert>

          {startedIds.size > 0 && (
            <Alert severity="warning">
              <Typography variant="body2" fontWeight={600}>
                {startedIds.size === selectedSessionList.length
                  ? `${startedIds.size === 1 ? 'This session starts' : `All ${startedIds.size} sessions start`} before the current time on ${copyTargetDate ? format(copyTargetDate, 'EEE, dd MMM') : 'that day'}.`
                  : `${startedIds.size} of these sessions start before the current time on ${copyTargetDate ? format(copyTargetDate, 'EEE, dd MMM') : 'that day'}.`}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                They can still be created, but members can't book a class that has already started.
              </Typography>
            </Alert>
          )}

          <Box sx={{ maxHeight: 320, overflowY: 'auto', border: `1px solid ${colors.divider}`, borderRadius: 1 }}>
            <Stack divider={<Divider />}>
              {selectedSessionList.map((s) => (
                <Box key={s._id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1 }}>
                  <Typography variant="body2" fontWeight={700} sx={{ width: 78, flexShrink: 0 }}>
                    {formatSessionTime(s.date)}
                  </Typography>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>{s.className}</Typography>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                      {s.instructor?.name || 'No instructor'} · {s.studio?.studioName || 'N/A'}
                    </Typography>
                  </Box>
                  {startedIds.has(s._id) && (
                    <Chip label="Already started" size="small" color="warning" variant="outlined" />
                  )}
                  <Chip label={`${s.slots} slots`} size="small" variant="outlined" />
                  <Chip label={`₹${s.price}`} size="small" color="primary" variant="outlined" />
                </Box>
              ))}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        {startedIds.size > 0 && startedIds.size < selectedSessionList.length && (
          <Button onClick={() => handleCopyDay(false)} disabled={isCopying}>
            {`Copy without them (${selectedSessionList.length - startedIds.size})`}
          </Button>
        )}
        <Button
          onClick={() => handleCopyDay(true)}
          variant="contained"
          disabled={isCopying || !copyTargetDate || selectedSessionList.length === 0}
          startIcon={isCopying ? <CircularProgress size={18} /> : null}
        >
          {isCopying
            ? 'Copying…'
            : startedIds.size > 0
              ? `Copy all ${selectedSessionList.length} anyway`
              : `Confirm copy ${selectedSessionList.length} session${selectedSessionList.length !== 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>

    <DuplicateSessionConfirmDialog
      open={!!dupInfo}
      info={dupInfo}
      loading={dupLoading}
      totalRequested={pendingCopy?.sessions.length}
      onCancel={() => { setDupInfo(null); setPendingCopy(null); }}
      onConfirm={handleConfirmDuplicate}
    />
    </>
  );
};

export default CopyDayDialog;
